import React, { useMemo, useRef, useState, useCallback } from "react";
import { DragOutlined, ExportOutlined } from "@ant-design/icons";
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Locale, Util } from "../../../../utils";
import zhCN from "../../../../utils/locale/zh-CN";
import ugCN from "../../../../utils/locale/ug-CN";
import {
  ensureIndexColumn,
  flattenRowChildren,
  packRowsOnly,
  PRODUCT_COLUMNS,
} from "../catalog";
import { SAMPLE_ORDER } from "../sampleOrder";
import BarcodeDisplay from "./BarcodeDisplay";
import "./TemplateCanvas.scss";

const i18n = Locale.getInstance();
const util = Util.getInstance();

/** Translate a key for template content language (independent of UI locale). */
function tLang(key, lang) {
  const dict = lang === "ug" ? ugCN : zhCN;
  const parts = String(key || "").split(".");
  let cur = dict;
  for (const part of parts) {
    if (cur == null || typeof cur !== "object") return key;
    cur = cur[part];
  }
  return typeof cur === "string" ? cur : key;
}

function columnHeaderLabel(col, lang) {
  const meta = PRODUCT_COLUMNS.find((c) => c.key === col.key);
  if (!meta?.labelKey) return col.label || "";
  const translated = tLang(meta.labelKey, lang);
  const zhDefault = tLang(meta.labelKey, "zh");
  const ugDefault = tLang(meta.labelKey, "ug");
  if (
    col.label &&
    col.label !== zhDefault &&
    col.label !== ugDefault &&
    col.label !== meta.defaultLabel
  ) {
    return col.label;
  }
  return translated;
}

function fieldLabel(comp, lang) {
  if (comp.showLabel === false) return "";
  if (comp.label) {
    const translated = tLang(comp.label, lang);
    const zhDefault = tLang(comp.label, "zh");
    const ugDefault = tLang(comp.label, "ug");
    if (
      comp.labelText &&
      comp.labelText !== zhDefault &&
      comp.labelText !== ugDefault
    ) {
      // Custom Chinese overrides still switch to i18n for ug templates.
      if (lang === "ug" && /[\u4e00-\u9fff]/.test(comp.labelText)) {
        return translated;
      }
      return comp.labelText;
    }
    return translated;
  }
  return comp.labelText || "";
}

function summaryRowLabel(comp, lang) {
  const translated = tLang("order.printView.total", lang);
  const custom = comp.summaryLabel;
  if (
    custom &&
    custom !== "合计" &&
    custom !== tLang("order.printView.total", "zh") &&
    custom !== tLang("order.printView.total", "ug")
  ) {
    if (lang === "ug" && /[\u4e00-\u9fff]/.test(custom)) {
      return translated;
    }
    return custom;
  }
  return translated;
}

/** Map designer align to logical CSS so RTL templates flip start/end. */
function cssTextAlign(align) {
  if (align === "center") return "center";
  if (align === "right") return "end";
  return "start";
}

function propByLang(obj, prop, lang) {
  if (!obj) return "";
  const ug = obj[`ug_${prop}`];
  const zh = obj[`zh_${prop}`];
  if (lang === "ug") {
    return ug || obj[prop] || zh || "";
  }
  return zh || obj[prop] || ug || "";
}

function resolveField(order, field, lang = "zh") {
  if (!order) return "";
  switch (field) {
    case "merchant_name":
      return (
        propByLang(order.merchant, "name", lang) ||
        order.merchant?.name ||
        ""
      );
    case "merchant_tel":
      return order.merchant?.tel || "";
    case "receiver":
      return order.receiver || "";
    case "tel":
      return order.tel || "";
    case "address":
      return lang === "ug"
        ? order.ug_address || order.address || order.zh_address || ""
        : order.zh_address || order.address || order.ug_address || "";
    case "notes":
      return order.notes || "";
    case "order_no":
      return order.order_no || String(order.id || "");
    case "created_at":
      return order.created_at || "";
    case "print_time":
      return new Date().toLocaleString();
    case "paytype_label":
      return order.paytype_label || "";
    case "ship_price":
      return util.getFormattedPrice(order.ship_price || 0, "¥");
    case "total":
      return util.getFormattedPrice(order.total || 0, "¥");
    case "order_url":
      return order.public_qrcode_url || order.order_url || "";
    default:
      return order[field] ?? "";
  }
}

function isHttpUrl(value) {
  return /^https?:\/\//i.test(String(value || "").trim());
}

function cellValue(detail, key, index, lang = "zh") {
  const name = propByLang(detail.product, "name", lang);
  const sku = propByLang(detail.selected_sku, "name", lang);
  const unit = detail.base_unit
    ? propByLang(detail.base_unit, "name", lang)
    : "";
  const barcode =
    detail.barcode ||
    detail.selected_sku?.barcode ||
    detail.product?.barcode ||
    "";
  const line = detail.amount * detail.price;
  switch (key) {
    case "index":
      return index + 1;
    case "image":
      return "🖼";
    case "name":
      return name;
    case "sku":
      return sku;
    case "name_sku":
      return sku ? `${name} / ${sku}` : name;
    case "barcode":
      return barcode;
    case "unit":
      return unit;
    case "amount":
      return unit ? `${detail.amount}${unit}` : detail.amount;
    case "price":
      return util.getFormattedPrice(detail.price, "¥");
    case "line_total":
      return util.getFormattedPrice(line, "¥");
    case "remark":
      return detail.remark || "";
    default:
      return "";
  }
}

function CompBody({ comp, order, lang = "zh" }) {
  if (comp.type === "divider") {
    return <hr style={{ borderTopWidth: comp.thickness || 1 }} />;
  }
  if (comp.type === "spacer") {
    return <div style={{ height: comp.height || 12 }} />;
  }
  if (comp.type === "image") {
    return (
      <div className="tpl-comp__image-box" style={{ height: comp.height || 48 }}>
        {comp.url ? <img src={comp.url} alt="" /> : <span>LOGO</span>}
      </div>
    );
  }
  if (comp.type === "barcode") {
    const val = resolveField(order, comp.field, lang) || comp.value || "";
    const barcodeFormat =
      comp.barcodeFormat || (comp.field === "order_no" ? "qr" : "code128");
    return (
      <>
        <BarcodeDisplay
          value={val}
          height={comp.height || (barcodeFormat === "qr" ? 120 : 48)}
          size={comp.size || comp.height || 120}
          align={comp.align || "center"}
          format={barcodeFormat}
        />
        {comp.showValue !== false ? (
          <div className="tpl-comp__barcode-val">{val}</div>
        ) : null}
      </>
    );
  }
  if (comp.type === "qrcode") {
    const val = resolveField(order, comp.field, lang) || comp.value || "";
    const size = comp.size || 80;
    // Miniapp wxacode is an image URL; generic QR encodes a string payload
    if (comp.field === "order_url" && (order?.public_qrcode_url || isHttpUrl(val))) {
      const src = order?.public_qrcode_url || val;
      return (
        <div
          className="tpl-comp__qr-img"
          style={{ textAlign: cssTextAlign(comp.align || "center") }}
        >
          <img
            src={src}
            alt=""
            style={{
              width: size,
              height: size,
              objectFit: "contain",
              display: "inline-block",
            }}
          />
        </div>
      );
    }
    return val ? (
      <BarcodeDisplay
        value={val}
        format="qr"
        size={size}
        height={size}
        align={comp.align || "center"}
      />
    ) : (
      <div
        className="tpl-comp__qr"
        style={{ width: size, height: size }}
      >
        QR
      </div>
    );
  }
  if (comp.type === "product-table") {
    const cols = ensureIndexColumn(comp.columns || []);
    const details = order?.details || [];
    let sumAmount = 0;
    let sumTotal = 0;
    details.forEach((d) => {
      sumAmount += Number(d.amount) || 0;
      sumTotal += (Number(d.amount) || 0) * (Number(d.price) || 0);
    });
    return (
      <table className={comp.border === "none" ? "no-border" : ""}>
        {comp.showHeader !== false ? (
          <thead>
            <tr>
              {cols.map((c) => (
                <th
                  key={c.key}
                  style={{
                    width: `${c.width}%`,
                    fontSize: comp.headerFontSize || 9,
                    fontWeight: comp.headerBold ? 700 : 400,
                  }}
                >
                  {columnHeaderLabel(c, lang)}
                </th>
              ))}
            </tr>
          </thead>
        ) : null}
        <tbody>
          {details.map((d, i) => (
            <tr key={i}>
              {cols.map((c) => (
                <td
                  key={c.key}
                  style={{
                    fontSize: comp.bodyFontSize || 9,
                    fontWeight: comp.bodyBold ? 700 : 400,
                  }}
                >
                  {cellValue(d, c.key, i, lang)}
                </td>
              ))}
            </tr>
          ))}
          {comp.showSummary ? (
            <tr className="tpl-comp__summary">
              {cols.map((c, i) => {
                let text = "";
                if (i === 0) text = summaryRowLabel(comp, lang);
                if ((comp.summaryColumns || []).includes(c.key)) {
                  if (c.key === "amount") text = sumAmount;
                  if (c.key === "line_total")
                    text = util.getFormattedPrice(sumTotal, "¥");
                }
                return (
                  <td
                    key={c.key}
                    style={{ fontSize: comp.bodyFontSize || 9, fontWeight: 700 }}
                  >
                    {text}
                  </td>
                );
              })}
            </tr>
          ) : null}
        </tbody>
      </table>
    );
  }

  let content = "";
  if (comp.type === "custom-text") {
    content = comp.text || tLang("order.printTemplate.comps.customText", lang);
  } else {
    const val = resolveField(order, comp.field, lang);
    const label = fieldLabel(comp, lang);
    content = label ? `${label}: ${val}` : val;
  }
  return content || "\u00A0";
}

function typeClass(type) {
  switch (type) {
    case "divider":
      return "tpl-comp--divider";
    case "spacer":
      return "tpl-comp--spacer";
    case "image":
      return "tpl-comp--image";
    case "barcode":
      return "tpl-comp--barcode";
    case "qrcode":
      return "tpl-comp--qr";
    case "product-table":
      return "tpl-comp--table";
    default:
      return "tpl-comp--text";
  }
}

/** Drag item before/after others; column-snap resize when selected. */
function SortableLeaf({
  comp,
  order,
  lang = "zh",
  selected,
  onSelect,
  pageRef,
  onSpanChange,
  nested = false,
  displaySpan,
  resizeId,
  onResizePreview,
  onEjectFromBeside,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: comp.id });

  const span = displaySpan ?? comp.span ?? 12;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    gridColumn: `span ${span}`,
    marginTop: comp.marginTop || 0,
    fontSize: comp.fontSize || 12,
    fontWeight: comp.bold ? 700 : 400,
    textAlign: cssTextAlign(comp.align || "left"),
  };

  const startResize = useCallback(
    (edge) => (e) => {
      if (!onSpanChange || !pageRef?.current) return;
      e.preventDefault();
      e.stopPropagation();
      const page = pageRef.current;
      const host = nested
        ? e.currentTarget.closest(".tpl-comp--stack") || page
        : page;
      const rect = host.getBoundingClientRect();
      const colW = rect.width / 12;
      const startX = e.clientX;
      const startSpan = displaySpan ?? comp.span ?? 12;
      page.classList.add("is-resizing");
      const targetId = resizeId || comp.id;

      const apply = (clientX) => {
        const dx = clientX - startX;
        const dCols = Math.round(dx / colW);
        let next =
          edge === "right" ? startSpan + dCols : startSpan - dCols;
        return Math.max(1, Math.min(12, next));
      };

      const onMove = (ev) => {
        const next = apply(ev.clientX);
        onResizePreview?.(targetId, next);
      };
      const onUp = (ev) => {
        page.classList.remove("is-resizing");
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        const next = apply(ev.clientX);
        onResizePreview?.(null, null);
        if (next !== startSpan) onSpanChange(targetId, next);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [
      comp.id,
      comp.span,
      displaySpan,
      nested,
      onSpanChange,
      onResizePreview,
      pageRef,
      resizeId,
    ]
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`tpl-comp ${typeClass(comp.type)} ${
        selected ? "is-selected" : ""
      } ${isDragging ? "is-dragging" : ""} ${nested ? "is-nested" : ""}`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.(comp.id);
      }}
    >
      <button
        type="button"
        className="tpl-comp__drag-handle"
        title={i18n.t("order.printTemplate.dragToReorder")}
        onClick={(e) => e.stopPropagation()}
        {...attributes}
        {...listeners}
      >
        <DragOutlined />
      </button>

      {nested && onEjectFromBeside ? (
        <button
          type="button"
          className="tpl-comp__eject"
          title={i18n.t("order.printTemplate.ejectFromBeside")}
          onClick={(e) => {
            e.stopPropagation();
            onEjectFromBeside(comp.id);
          }}
        >
          <ExportOutlined />
        </button>
      ) : null}

      {selected && onSpanChange ? (
        <>
          <div
            className="tpl-comp__resize tpl-comp__resize--left"
            onPointerDown={startResize("left")}
          />
          <div
            className="tpl-comp__resize tpl-comp__resize--right"
            onPointerDown={startResize("right")}
          />
          <div className="tpl-comp__span-badge">{span}/12</div>
        </>
      ) : null}

      <CompBody comp={comp} order={order} lang={lang} />
    </div>
  );
}

function StaticLeaf({ comp, order, lang = "zh", nested = false }) {
  const style = {
    gridColumn: `span ${comp.span || 12}`,
    marginTop: comp.marginTop || 0,
    fontSize: comp.fontSize || 12,
    fontWeight: comp.bold ? 700 : 400,
    textAlign: cssTextAlign(comp.align || "left"),
  };
  return (
    <div className={`tpl-comp ${typeClass(comp.type)}`} style={style}>
      <CompBody comp={comp} order={order} lang={lang} />
    </div>
  );
}

function CanvasRow({
  row,
  order,
  lang = "zh",
  selectedId,
  onSelect,
  pageRef,
  onSpanChange,
  onResizePreview,
  liveSpans,
  editable,
  onEjectFromBeside,
}) {
  const spanOf = (id, fallback) =>
    liveSpans?.[id] != null ? liveSpans[id] : fallback;

  return (
    <div className="template-canvas__row">
      {(row.children || []).map((cell) => {
        if (cell.type === "stack") {
          const stackSpan = spanOf(cell.id, cell.span || 12);
          const innerRows = packRowsOnly(cell.children || []);
          return (
            <div
              key={cell.id}
              className={`tpl-comp tpl-comp--stack ${
                (cell.children || []).some((c) => c.id === selectedId)
                  ? "is-selected"
                  : ""
              }`}
              style={{
                gridColumn: `span ${stackSpan}`,
                marginTop: cell.marginTop || 0,
              }}
            >
              {editable ? (
                <div className="tpl-comp__stack-label">
                  {i18n.t("order.printTemplate.sideStack")}
                </div>
              ) : null}
              {innerRows.map((innerRow) => (
                <div key={innerRow.id} className="tpl-comp__stack-row">
                  {(innerRow.children || []).map((child) => {
                    const childSpan = spanOf(child.id, child.span || 12);
                    return editable ? (
                      <SortableLeaf
                        key={child.id}
                        comp={{ ...child, span: childSpan }}
                        order={order}
                        lang={lang}
                        selected={selectedId === child.id}
                        onSelect={onSelect}
                        pageRef={pageRef}
                        onSpanChange={onSpanChange}
                        onResizePreview={onResizePreview}
                        nested
                        displaySpan={childSpan}
                        resizeId={child.id}
                        onEjectFromBeside={onEjectFromBeside}
                      />
                    ) : (
                      <StaticLeaf
                        key={child.id}
                        comp={{ ...child, span: childSpan }}
                        order={order}
                        lang={lang}
                        nested
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          );
        }
        const cellSpan = spanOf(cell.id, cell.span || 12);
        return editable ? (
          <SortableLeaf
            key={cell.id}
            comp={{ ...cell, span: cellSpan }}
            order={order}
            lang={lang}
            selected={selectedId === cell.id}
            onSelect={onSelect}
            pageRef={pageRef}
            onSpanChange={onSpanChange}
            onResizePreview={onResizePreview}
            displaySpan={cellSpan}
            resizeId={cell.id}
          />
        ) : (
          <StaticLeaf key={cell.id} comp={cell} order={order} lang={lang} />
        );
      })}
    </div>
  );
}

export default function TemplateCanvas({
  template,
  order = SAMPLE_ORDER,
  selectedId,
  onSelect,
  onLeafDrag,
  onLeavesReorder,
  onEjectFromBeside,
  onSpanChange,
  zoom = 1,
  editable = true,
}) {
  const paper = template?.paperSize || "A4";
  const lang = template?.lang === "ug" ? "ug" : "zh";
  const rows = template?.layout?.rows || [];
  const pageRef = useRef(null);
  const [liveSpans, setLiveSpans] = useState({});

  const leafIds = useMemo(
    () =>
      rows.flatMap((r) =>
        flattenRowChildren(r.children || []).map((c) => c.id)
      ),
    [rows]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const onResizePreview = useCallback((id, span) => {
    if (!id) {
      setLiveSpans({});
      return;
    }
    setLiveSpans({ [id]: span });
  }, []);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (onLeafDrag) {
      onLeafDrag(String(active.id), String(over.id));
    }
  };

  const canDrag = editable && onLeafDrag;

  const page = (
    <div
      ref={pageRef}
      className="template-canvas__page"
      dir={lang === "ug" ? "rtl" : "ltr"}
      lang={lang === "ug" ? "ug" : "zh"}
      onClick={() => onSelect?.(null)}
    >
      <div className="template-canvas__col-guides" aria-hidden>
        {Array.from({ length: 12 }).map((_, i) => (
          <span key={i} />
        ))}
      </div>

      {canDrag ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={leafIds} strategy={rectSortingStrategy}>
            {rows.map((row) => (
              <CanvasRow
                key={row.id}
                row={row}
                order={order}
                lang={lang}
                selectedId={selectedId}
                onSelect={onSelect}
                pageRef={pageRef}
                onSpanChange={onSpanChange}
                onResizePreview={onResizePreview}
                liveSpans={liveSpans}
                editable
                onEjectFromBeside={onEjectFromBeside}
              />
            ))}
          </SortableContext>
        </DndContext>
      ) : (
        rows.map((row) => (
          <CanvasRow
            key={row.id}
            row={row}
            order={order}
            lang={lang}
            selectedId={null}
            onSelect={undefined}
            pageRef={pageRef}
            onSpanChange={undefined}
            onResizePreview={undefined}
            liveSpans={{}}
            editable={false}
          />
        ))
      )}

      {!rows.length ? (
        <div className="template-canvas__empty">
          {i18n.t("order.printTemplate.emptyCanvas")}
        </div>
      ) : null}
    </div>
  );

  return (
    <div
      className={`template-canvas paper-${paper}${
        editable ? "" : " template-canvas--print"
      }${lang === "ug" ? " template-canvas--ug" : ""}`}
      style={{ zoom }}
    >
      {page}
    </div>
  );
}
